import type { PageSection } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import { EmptyState } from '@crowdex/ui/components/feedback';
import { FolderX } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import type { NativePluginContext } from '@/features/plugins/native-plugin';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { contributionId, type PluginPanel } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { openAppHome } from '@/lib/app-home';
import { closeAppPanelTab, restorePanel, setAppPanelTarget } from '@/lib/desktop-shell/app-panels';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { askAgent, attachToAgent } from './agent-ask';
import { openAppDialog } from './app-dialog';
import { AppFrame, type AppFrameProps } from './app-frame';
import {
  appSections,
  forgetLiveSections,
  pickLiveItem,
  refreshAppSidebar,
  setLiveSections,
  useAppSectionItems,
  useLiveSections,
  useLiveSelection,
} from './app-panel-sidebar';
import { useAvatarOrigin } from './artifact-origin';
import { isCoreOpenTarget, useOpenCoreTarget } from './core-open-targets';
import { dockArtifactTarget } from './dock-artifact-target';
import { usePluginUse } from './plugin-context';
import { PluginFrame, useViewHeader, viewHeaderActions } from './plugin-frame';
import { SectionsPicker } from './sections-picker';
import { useAppConversation } from './use-app-conversation';
import { inspectorView, useViewInspector, withInspector } from './view-inspector';

/**
 * A tab of an app docked in the side panel of the conversation on screen, working in that
 * conversation's workspace, in the host's frame (`PluginFrame`: its title, the view's actions,
 * ⋯ with Open full screen, Settings and Close). It shows its own item, picked in the frame's
 * sections picker (the app's sections, `page.sidebars`: `context.item`, `context.section`; the
 * main sidebar stays the assistants'), and tells the core the live sections of what it shows
 * (`page.sections.set`), which the picker lists too; what the user picks there reaches it as
 * `context.selection`. An app has as many tabs as items shown side by side.
 */
export function AppViewPanel({
  panel,
  tabId,
  current,
  context,
  onTitleChange,
}: {
  panel: PluginPanel & { appView: NonNullable<PluginPanel['appView']> };
  tabId: string;
  /** The app's tab shown last (of its tabs shown side by side): its item is the agent's context. */
  current: boolean;
  context: NativePluginContext;
  /** The title of the item it shows (`null`: none), which names its tab. */
  onTitleChange?: ((title: string | null) => void) | undefined;
}) {
  const store = useDesktopShellStoreApi();
  const openCoreTarget = useOpenCoreTarget();
  const workbench = useWorkbench();
  // The conversation shown is the one the app works in: saved for it when it is a new one.
  const conversation = useAppConversation(
    context.taskId || undefined,
    workbench.pluginDisplayNames[panel.appView.plugin] ?? panel.title,
    !context.preview,
  );
  const workspace = conversation.state === 'saved' ? conversation.conversation : undefined;
  const target = useDesktopShellStore((state) => state.appPanelTargets[tabId]);
  const item = target?.item ?? null;
  const sections = appSections(panel);
  const section = sections.find((entry) => entry.id === target?.section) ?? sections[0] ?? null;
  const sidebar = useAppSectionItems(panel, item === null ? null : section);
  const selected = sidebar.items.find((entry) => entry.id === item);
  const title = selected?.title ?? null;
  useEffect(() => onTitleChange?.(title), [onTitleChange, title]);
  const selection = useLiveSelection(tabId);
  const liveSections = useLiveSections(tabId);
  const viewHeader = useViewHeader();
  const inspector = useViewInspector();
  const origin = useAvatarOrigin(panel.appView.plugin, item);
  // Its live sections are its own: gone with it.
  useEffect(() => () => forgetLiveSections(tabId), [tabId]);
  // The app shown is the context of the open conversation's agent.
  usePluginUse(context.visible && current && workspace !== undefined ? workspace : null, {
    plugin: panel.appView.plugin,
    title: workbench.pluginDisplayNames[panel.appView.plugin] ?? panel.title,
    ...(item === null ? {} : { item: { id: item, title: selected?.title } }),
    skills: workbench.pluginSkills[panel.appView.plugin] ?? [],
  });
  const select = useCallback(
    (next: string | null, nextSection?: string) => {
      setAppPanelTarget(store, tabId, {
        item: next,
        ...((nextSection ?? sections[0]?.id) === undefined
          ? {}
          : { section: nextSection ?? sections[0]?.id }),
      });
      refreshAppSidebar(panel.view);
    },
    [store, panel.view, tabId, sections],
  );
  const refresh = useCallback(() => refreshAppSidebar(panel.view), [panel.view]);
  const setSections = useCallback((next: PageSection[]) => setLiveSections(tabId, next), [tabId]);
  const navigate = useCallback(
    (route: string) => {
      const current = store.getState().appPanelTargets[tabId];
      setAppPanelTarget(store, tabId, { ...(current ?? { item: null }), route });
    },
    [store, tabId],
  );
  const back = useCallback(() => {
    const current = store.getState().appPanelTargets[tabId];
    if (current?.route !== undefined) {
      const parts = current.route.split('/').filter(Boolean);
      const { route: _route, ...rest } = current;
      setAppPanelTarget(
        store,
        tabId,
        parts.length > 1 ? { ...rest, route: `/${parts.slice(0, -1).join('/')}` } : rest,
      );
    } else if (current !== undefined && current.item !== null) select(null, current.section);
  }, [store, tabId, select]);
  const appTitle = workbench.pluginDisplayNames[panel.appView.plugin] ?? panel.title;
  const frame = (children: React.ReactNode, view?: AppFrameProps) => (
    <PluginFrame
      actions={viewHeaderActions(viewHeader.header, viewHeader.emit)}
      icon={pluginIcon(panel.icon ?? 'app')}
      iconSrc={panel.iconSrc}
      inspector={view === undefined ? null : inspectorView(inspector, view)}
      layout="docked"
      onBack={target?.route !== undefined || item !== null ? back : undefined}
      backLabel={appTitle}
      onClose={() => closeAppPanelTab(store, tabId)}
      openElsewhere={{
        label: 'Open full screen',
        onSelect: () =>
          openAppHome(panel.appView.plugin, item, {
            route: target?.route ?? (item === null ? null : (section?.id ?? null)),
          }),
      }}
      picker={
        <SectionsPicker
          item={item}
          liveSections={liveSections}
          onPickLive={(live, picked) => pickLiveItem(tabId, live, picked)}
          onSelect={select}
          panel={panel}
          section={section}
          sections={sections}
        />
      }
      plugin={panel.appView.plugin}
      status={viewHeader.header?.status}
      title={viewHeader.header?.title ?? title ?? appTitle}
    >
      {children}
    </PluginFrame>
  );
  if (conversation.state === 'saving') return <main aria-busy className="h-full bg-background" />;
  if (conversation.state === 'failed')
    return (
      <main className="flex h-full items-center justify-center bg-background">
        <EmptyState
          action={
            <Button onClick={conversation.retry} size="sm" variant="outline">
              Try again
            </Button>
          }
          description={conversation.message}
          icon={<FolderX />}
          title={`${panel.title} has no folder to work in`}
        />
      </main>
    );
  const view = withInspector(inspector, {
    action: viewHeader.action,
    container: 'side',
    layout: 'docked',
    origin,
    plugin: panel.appView.plugin,
    fragment: target?.route,
    route: target?.route ?? '/',
    handlers: {
      // The conversation shows beside the app (no longer under it) with the request.
      ask: (text) => {
        restorePanel(store);
        askAgent(text, context.taskId);
      },
      attach: (selections, send) => {
        restorePanel(store);
        attachToAgent(selections, send, context.taskId);
      },
      open: (opened) => {
        if (isCoreOpenTarget(opened)) openCoreTarget(opened);
        // An artifact docks beside the conversation, in its own tab.
        else if ('artifact' in opened)
          void dockArtifactTarget(store, {
            plugin: panel.appView.plugin,
            ...opened.artifact,
            ...(workspace === undefined ? {} : { conversation: workspace }),
          });
        else if (opened.in === 'dialog')
          return openAppDialog({
            plugin: panel.appView.plugin,
            path: opened.path,
            title: panel.title,
            input: opened.input,
            size: opened.size,
            workspace,
          });
        else context.openPanel?.(contributionId(panel.plugin, panel.id));
        return null;
      },
      closeDialog: () => {
        throw new Error('This view is not a dialog.');
      },
      confirm: ({ title, message }) => window.confirm(message ? `${title}\n\n${message}` : title),
      selectItem: select,
      refreshPage: refresh,
      setSections,
      setHeader: viewHeader.setHeader,
      navigate,
      back,
    },
    item: panel.rail === undefined ? undefined : item,
    section: panel.rail === undefined || item === null ? undefined : (section?.id ?? null),
    selection,
    media: panel.appView.media,
    path: panel.appView.path,
    title: panel.title,
    workspace,
  });
  return frame(<AppFrame {...view} />, view);
}
